import { type Form as FormType } from '@wix/ambassador-forms-v4-form/types';
import {
  getCountryField,
  getCountryFieldLayout,
  type CountryCode,
} from '@wix/form-multiline-address';
import { addFieldToSchema, generateUUID } from '@wix/form-schema';
import { type ValidationError } from '@wix/form-validator';
import React, { type ReactNode, useCallback, useMemo } from 'react';

import { EXPERIMENTS, FIELD_TYPES } from '../../../constants';
import { useAddressForms } from '../../../contexts/address-forms-context';
import { useFocusGroupFieldEvent } from '../../../contexts/events-context';
import { useExperiments } from '../../../contexts/experiments-context';
import { useFieldActions } from '../../../contexts/field-actions-context';
import { useFieldPropsV2 } from '../../../contexts/field-props-context';
import { useFieldValidation } from '../../../contexts/field-validation-context';
import { useForm } from '../../../contexts/form-context';
import {
  ADDRESS_TARGETS,
  type Address,
  fromAtlasAddress,
  type AddressCamel,
} from '../../../fields/multiline-address/multiline-address/address';
import {
  AddressAutofillProvider,
  type onAddressSelect,
} from '../../../fields/multiline-address/multiline-address/address-autofill-context';
import { CountryCodeProvider } from '../../../fields/multiline-address/multiline-address/country-code-context';
import { removeGaps } from '../../../fields/multiline-address/multiline-address/remove-gaps';
import { findChildOfType } from '../../../services/find-child';
import { createFormView } from '../../../services/form-view';
import { type InputFieldProps } from '../../../types';
import { applyMultilineAddressSettings } from '../../../utils/apply-multiline-address-settings';
import { overrideFieldIds } from '../../../utils/field-id-overrides';
import { Form } from '../../form/components/form';
import type { PossibleValues } from '../../form/types';

import {
  AddressFieldsProvider,
  useAddressFields,
} from './address-fields-context';
import { AddressLine2 } from './address-line-2-field-headless';
import { AddressLine } from './address-line-field-headless';
import { City } from './city-field-headless';
import { Country } from './country-field-headless';
import { PostalCode } from './postal-code-field-headless';
import { StreetName } from './street-name-field-headless';
import { StreetNumber } from './street-number-field-headless';
import { Subdivision } from './subdivision-field-headless';

// MultilineAddress value combines both camelCase and snake_case
export type MultilineAddressValue = Address & AddressCamel;

export type MultilineAddressFieldProps =
  InputFieldProps<MultilineAddressValue> & {
    fieldSettings: Record<string, any>;
    autocompleteEnabled?: boolean;
    hideLabel?: boolean;
  };

export const MlaCountryWrapper = () => useAddressFields('country');
export const MlaSubdivisionWrapper = () => useAddressFields('subdivision');
export const MlaCityWrapper = () => useAddressFields('city');
export const MlaAddressLineWrapper = () => useAddressFields('addressLine');
export const MlaAddressLine2Wrapper = () => useAddressFields('addressLine2');
export const MlaPostalCodeWrapper = () => useAddressFields('postalCode');
export const MlaStreetNameWrapper = () => useAddressFields('streetName');
export const MlaStreetNumberWrapper = () => useAddressFields('streetNumber');

const ADDRESS_FIELDS = {
  [FIELD_TYPES.MLA_COUNTRY]: MlaCountryWrapper,
  [FIELD_TYPES.MLA_SUBDIVISION]: MlaSubdivisionWrapper,
  [FIELD_TYPES.MLA_CITY]: MlaCityWrapper,
  [FIELD_TYPES.MLA_ADDRESS_LINE]: MlaAddressLineWrapper,
  [FIELD_TYPES.MLA_ADDRESS_LINE_2]: MlaAddressLine2Wrapper,
  [FIELD_TYPES.MLA_POSTAL_CODE]: MlaPostalCodeWrapper,
  [FIELD_TYPES.MLA_STREET_NAME]: MlaStreetNameWrapper,
  [FIELD_TYPES.MLA_STREET_NUMBER]: MlaStreetNumberWrapper,
};

const NO_VALUES = {};
const NO_ERRORS: ValidationError[] = [];
const FIELD_IDS = {
  country: generateUUID(),
  subdivision: generateUUID(),
  city: generateUUID(),
  address_line: generateUUID(),
  address_line_2: generateUUID(),
  postal_code: generateUUID(),
  street_name: generateUUID(),
  street_number: generateUUID(),
  apartment: generateUUID(),
};

export type MultilineAddressProps = {
  children: ReactNode;
};

export const MultilineAddress = ({ children }: MultilineAddressProps) => {
  const {
    id,
    form: _form,
    value,
    onChange,
    errors,
    FieldLayout,
    fieldSettings,
    label,
    validation,
    target,
    autocompleteEnabled,
    required,
    fieldType,
  } = useFieldPropsV2<MultilineAddressFieldProps>();

  const { isExperimentEnabled } = useExperiments();
  const useFieldsV2 = isExperimentEnabled(EXPERIMENTS.USE_FIELDS_V2);
  const { getCountryAddressForm } = useAddressForms();
  const { validateFields: parentValidateFields } = useFieldValidation();
  const formSchema = useForm();
  const fieldActions = useFieldActions();
  const template = getCountryAddressForm(value?.country);
  const isMultilineAddressField =
    fieldType === FIELD_TYPES.MULTILINE_ADDRESS ||
    fieldType === FIELD_TYPES.BOOKINGS_ADDRESS;

  const mlaCountryField = useMemo(
    () =>
      getCountryField(
        required,
        validation?.predefined?.multilineAddressOptions,
      ),
    [validation, required],
  );

  const mlaCountryLayout = useMemo(() => getCountryFieldLayout(), []);

  const form = useMemo(() => {
    if (!isMultilineAddressField || !template) {
      return (
        _form && {
          ..._form,
          fields: overrideFieldIds(_form.fields, FIELD_IDS),
        }
      );
    }

    const baseForm = createFormView({
      useFieldsV2,
      path: target,
      parentId: id,
      form: applyMultilineAddressSettings(
        addFieldToSchema(
          template,
          mlaCountryField,
          mlaCountryLayout,
        ) as FormType,
        fieldSettings,
        (validation?.predefined as any)?.multilineAddressOptions.fields,
      ),
    });

    return baseForm.fields.some((field) => field.properties.hidden)
      ? removeGaps(baseForm)
      : baseForm;
  }, [
    template,
    _form,
    fieldSettings,
    validation,
    target,
    mlaCountryField,
    mlaCountryLayout,
    isMultilineAddressField,
    useFieldsV2,
    id,
  ]);

  const handleAddressSelect: onAddressSelect = useCallback(
    (atlasAddress) => {
      if (!value) {
        return;
      }
      const address = fromAtlasAddress(atlasAddress, isMultilineAddressField);

      const newValue = {
        ...address,
        [ADDRESS_TARGETS.COUNTRY]:
          address[ADDRESS_TARGETS.COUNTRY] ?? value[ADDRESS_TARGETS.COUNTRY],
      };
      onChange?.(newValue);
    },
    [onChange, value, isMultilineAddressField],
  );

  const handleFieldChange = useCallback(
    (_target: string, subFieldValue: PossibleValues) => {
      if (
        isMultilineAddressField &&
        _target === ADDRESS_TARGETS.COUNTRY &&
        value?.[_target] !== subFieldValue
      ) {
        onChange?.({ [ADDRESS_TARGETS.COUNTRY]: subFieldValue as CountryCode });
        return;
      }

      onChange?.({
        ...value,
        [_target]: subFieldValue,
      });
    },
    [onChange, value, isMultilineAddressField],
  );

  const validateFields = useCallback(
    async (fieldsToValidate: { path: string; value: PossibleValues }[]) => {
      if (!isMultilineAddressField) {
        return parentValidateFields(fieldsToValidate);
      }

      return parentValidateFields([
        ...fieldsToValidate,
        { path: `${target}/${ADDRESS_TARGETS.COUNTRY}`, value: value?.country },
      ]);
    },
    [value, target, parentValidateFields, isMultilineAddressField],
  );

  useFocusGroupFieldEvent(id, () => {
    const firstError = errors?.[0];
    const field = form?.fields?.find(
      (f) => 'target' in f && f.target === firstError?.errorPath,
    );
    return field?.id;
  });

  // Extract child field components
  const countryElement = findChildOfType(children, MultilineAddress.Country);
  const subdivisionElement = findChildOfType(
    children,
    MultilineAddress.Subdivision,
  );
  const cityElement = findChildOfType(children, MultilineAddress.City);
  const addressLineElement = findChildOfType(
    children,
    MultilineAddress.AddressLine,
  );
  const addressLine2Element = findChildOfType(
    children,
    MultilineAddress.AddressLine2,
  );
  const postalCodeElement = findChildOfType(
    children,
    MultilineAddress.PostalCode,
  );
  const streetNameElement = findChildOfType(
    children,
    MultilineAddress.StreetName,
  );
  const streetNumberElement = findChildOfType(
    children,
    MultilineAddress.StreetNumber,
  );

  // Store field components in context for MLA wrapper components to access
  const addressFields = useMemo(
    () => ({
      country: countryElement,
      subdivision: subdivisionElement,
      city: cityElement,
      addressLine: addressLineElement,
      addressLine2: addressLine2Element,
      postalCode: postalCodeElement,
      streetName: streetNameElement,
      streetNumber: streetNumberElement,
    }),
    [
      countryElement,
      subdivisionElement,
      cityElement,
      addressLineElement,
      addressLine2Element,
      postalCodeElement,
      streetNameElement,
      streetNumberElement,
    ],
  );

  if (!form) {
    return null;
  }

  return (
    <FieldLayout fieldId={id}>
      <CountryCodeProvider countryCode={value?.country}>
        <AddressAutofillProvider
          autocompleteEnabled={autocompleteEnabled}
          onAddressSelect={handleAddressSelect}
        >
          <AddressFieldsProvider fields={addressFields}>
            <fieldset
              style={{
                border: 'none',
                padding: 0,
                margin: 0,
                minWidth: 0,
              }}
            >
              <legend
                style={{
                  position: 'absolute',
                  width: '1px',
                  height: '1px',
                  padding: 0,
                  margin: '-1px',
                  overflow: 'hidden',
                  clip: 'rect(0, 0, 0, 0)',
                  whiteSpace: 'nowrap',
                  border: 0,
                }}
              >
                {label}
              </legend>
              <Form
                formSchema={formSchema}
                form={form}
                values={value ?? NO_VALUES}
                onFieldChange={handleFieldChange}
                errors={errors ?? NO_ERRORS}
                validateFields={validateFields}
                fields={ADDRESS_FIELDS}
                fieldActions={fieldActions}
              />
            </fieldset>
          </AddressFieldsProvider>
        </AddressAutofillProvider>
      </CountryCodeProvider>
    </FieldLayout>
  );
};

MultilineAddress.Country = Country;
MultilineAddress.Subdivision = Subdivision;
MultilineAddress.City = City;
MultilineAddress.AddressLine = AddressLine;
MultilineAddress.AddressLine2 = AddressLine2;
MultilineAddress.PostalCode = PostalCode;
MultilineAddress.StreetName = StreetName;
MultilineAddress.StreetNumber = StreetNumber;
